import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { TextField } from '@adobe/react-spectrum';
import { DateComponent } from '../../RsdForms/CreateMoment/components/DateComponent';
import styles from './FeatureHoverPopup.module.scss';
import { mustBeValidUrl, isAllowedDemoUrl } from '../../Form/validators';
import { PAR_ID_MAX_LENGTH } from '../../../../utils/constants';

const DEMO_LINK_ERROR_MESSAGE =
  'Demos must be a share link to videos stored in our designated Frame repository (e.g. https://next.frame.io/ or https://f.io/xxx) or Figma (e.g. https://figma.com/)';

export interface FeatureHoverContent {
  title?: string;
  etaDate?: string;
  url?: string;
  fieldType?: 'demo' | 'prd' | 'name' | 'parId';
  // Name editing fields
  featureId?: string;
  featureName?: string;
  jiraLink?: string;
  placeholderText?: string;
  // PAR ID editing
  parId?: string;
  // Calendar behavior
  disableTBD?: boolean;
  disableNA?: boolean;
  onSave?: (data: {
    etaDate?: string;
    url?: string;
    featureName?: string;
    jiraLink?: string;
    parId?: string;
  }) => void;
}

interface FeatureHoverPopupProps {
  content: FeatureHoverContent;
  isVisible: boolean;
  position: { x: number; y: number };
  arrowDirection?: 'up' | 'down';
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onRequestClose?: () => void;
  onTabOut?: (direction: 'next' | 'prev') => void;
  popupRef?: React.RefObject<HTMLDivElement>;
  /** Element that triggered this popup — excluded from outside-click so it can handle its own toggle. */
  triggerRef?: React.RefObject<HTMLElement>;
}

const FeatureHoverPopup: React.FC<FeatureHoverPopupProps> = ({
  content,
  isVisible,
  position,
  arrowDirection = 'down',
  onMouseEnter,
  onMouseLeave,
  onRequestClose,
  onTabOut,
  popupRef,
  triggerRef
}) => {
  // Initialize etaDate with proper format for DateComponent
  const initializeEtaDate = (val: any) => {
    // If it's already in the right format, use it
    if (val && typeof val === 'object') return val;
    // Otherwise, return the raw value (DateComponent will handle it)
    return val || '';
  };

  const [etaDate, setEtaDate] = useState(initializeEtaDate(content.etaDate));
  const [url, setUrl] = useState(content.url || '');
  const [featureName, setFeatureName] = useState(content.featureName || '');
  const [jiraLink, setJiraLink] = useState(content.jiraLink || '');
  const [parId, setParId] = useState(content.parId || '');
  const [parIdError, setParIdError] = useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  // Unified URL validation using shared validator
  const isValidUrl = (value: string): boolean => !mustBeValidUrl(value);
  const isJiraUrl = (value: string): boolean =>
    /^https?:\/\/jira\.corp\.adobe\.com\/browse\/[A-Za-z]+-[0-9]+\/?$/i.test(
      (value || '').trim()
    );
  const urlError =
    jiraLink.trim().length > 0 &&
    !(isValidUrl(jiraLink.trim()) || isJiraUrl(jiraLink.trim()));

  // Demo URL validation: valid URL format AND allowed domain
  // Allowed: frame.io, next.frame.io, f.io, figma.com, www.figma.com, app.figma.com
  const isDemoUrl = (value: string): boolean =>
    isValidUrl(value.trim()) && isAllowedDemoUrl(value.trim());

  const demoUrlError =
    content.fieldType === 'demo' &&
    url.trim().length > 0 &&
    !isDemoUrl(url.trim());

  // PRD URL validation: standard URL format only (any valid http/https URL)
  const isPrdUrl = (value: string): boolean => isValidUrl(value.trim());

  const prdUrlError =
    content.fieldType === 'prd' &&
    url.trim().length > 0 &&
    !isPrdUrl(url.trim());

  const prdDemoUrlError = demoUrlError || prdUrlError;

  const serializeEtaValue = (val: any): string => {
    if (!val) return '';
    if (typeof val === 'string') return val.trim();
    if (typeof val === 'object') {
      if (val.type === 'special' && val.value) return String(val.value).trim();
      if (val.type === 'date' && val.date) return String(val.date).trim();
      if (val.type === 'months' && Array.isArray(val.months) && val.months[0])
        return String(val.months[0]).trim();
      if (
        val.type === 'quarters' &&
        Array.isArray(val.quarters) &&
        val.quarters[0]
      )
        return String(val.quarters[0]).trim();
    }
    return '';
  };

  // Update local state when content changes - RESET on every change to avoid stale data
  React.useEffect(() => {
    // Initialize etaDate with proper format for DateComponent
    const initializeEtaDate = (val: any) => {
      // If it's already in the right format, use it
      if (val && typeof val === 'object') return val;
      // Otherwise, return the raw value (DateComponent will handle it)
      return val || '';
    };

    const nextEta = initializeEtaDate(content.etaDate);
    if (serializeEtaValue(etaDate) !== serializeEtaValue(nextEta)) {
      setEtaDate(nextEta);
    }
    if ((url || '') !== (content.url || '')) {
      setUrl(content.url || '');
    }
    if ((featureName || '') !== (content.featureName || '')) {
      setFeatureName(content.featureName || '');
    }
    if ((jiraLink || '') !== (content.jiraLink || '')) {
      setJiraLink(content.jiraLink || '');
    }
    if ((parId || '') !== (content.parId || '')) {
      setParId(content.parId || '');
    }
  }, [
    content.etaDate,
    content.url,
    content.featureName,
    content.jiraLink,
    content.parId,
    content.fieldType, // Also reset when field type changes (demo vs prd)
    content.featureId // And when switching between features
  ]);

  const handleDateChange = (fieldName: string, value: any) => {
    setEtaDate(value || '');

    // Immediate save on calendar/input change to mirror RSD behavior
    if (content.onSave && content.fieldType !== 'name') {
      const formatEtaForSave = (val: any): string => {
        if (!val) return '';
        if (typeof val === 'string') return val;
        if (typeof val === 'object') {
          if (val.type === 'special' && val.value) return val.value as string;
          if (val.type === 'date' && val.date) return val.date as string; // YYYY-MM-DD
          if (
            val.type === 'months' &&
            Array.isArray(val.months) &&
            val.months[0]
          )
            return val.months[0] as string;
          if (
            val.type === 'quarters' &&
            Array.isArray(val.quarters) &&
            val.quarters[0]
          )
            return val.quarters[0] as string;
        }
        return '';
      };
      content.onSave({ etaDate: formatEtaForSave(value), url });
    }
  };

  const handleSave = () => {
    if (content.onSave) {
      if (content.fieldType === 'parId') {
        if (parId.trim() === '') return;
        content.onSave({ parId });
      } else if (content.fieldType === 'name') {
        content.onSave({ featureName, jiraLink });
      } else {
        const formatEtaForSave = (val: any): string => {
          if (!val) return '';
          if (typeof val === 'string') return val;
          if (typeof val === 'object') {
            if (val.type === 'special' && val.value) return val.value;
            if (val.type === 'date' && val.date) return val.date; // YYYY-MM-DD
            if (
              val.type === 'months' &&
              Array.isArray(val.months) &&
              val.months[0]
            )
              return val.months[0];
            if (
              val.type === 'quarters' &&
              Array.isArray(val.quarters) &&
              val.quarters[0]
            )
              return val.quarters[0];
          }
          return '';
        };

        content.onSave({ etaDate: formatEtaForSave(etaDate), url });
      }
    }
  };

  const handleSaveAndClose = () => {
    handleSave();
    if (onRequestClose) onRequestClose();
  };

  const fieldTypeLabel = content.fieldType?.toUpperCase() || 'DEMO';

  const popupContent = (
    <div
      className={styles.hoverPopup}
      style={{
        left: position.x,
        top: position.y,
        willChange: 'transform'
      }}
      ref={el => {
        // Assign to both refs
        (containerRef as any).current = el;
        if (popupRef) (popupRef as any).current = el;
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={() => {
        // For name/parId popups, do NOT close on hover out; keep open until outside click
        if (content.fieldType !== 'name' && content.fieldType !== 'parId') {
          // Existing behavior for DEMO/PRD: save on hover exit and let parent close
          handleSave();
          if (onMouseLeave) onMouseLeave();
        }
      }}
    >
      <div className={styles.upDown}>
        <div className={styles.detailContent}>
          {/* Arrow pointer */}
          <div
            className={`${styles.arrowPointer} ${
              arrowDirection === 'up'
                ? styles.arrowPointerUp
                : styles.arrowPointerDown
            }`}
          >
            <div
              className={`${styles.arrowShape} ${
                arrowDirection === 'up'
                  ? styles.arrowShapeUp
                  : styles.arrowShapeDown
              }`}
            ></div>
          </div>

          <div className={styles.iconsTitle}>
            {/* Content Section */}
            <div className={styles.contentSection}>
              <div className={styles.formFields}>
                {content.fieldType === 'parId' ? (
                  <div className={styles.fieldGroup}>
                    <div className={styles.fieldLabel}>PAR ID *</div>
                    <TextField
                      value={parId}
                      onChange={v => {
                        setParId(v.slice(0, PAR_ID_MAX_LENGTH));
                        if (v.trim()) setParIdError(false);
                      }}
                      maxLength={PAR_ID_MAX_LENGTH}
                      placeholder="e.g. PAR-4567"
                      UNSAFE_className={`${styles.nameInput}${
                        parIdError ? ` ${styles.nameInputError}` : ''
                      }`}
                      aria-label="PAR ID"
                      aria-invalid={parIdError}
                      onKeyDown={e => {
                        if ((e as any).key === 'Enter') {
                          e.preventDefault();
                          if (parId.trim() === '') {
                            setParIdError(true);
                            return;
                          }
                          handleSaveAndClose();
                        } else if ((e as any).key === 'Tab') {
                          e.preventDefault();
                          if (parId.trim() === '') {
                            setParIdError(true);
                            return;
                          }
                          handleSave();
                          if (onTabOut)
                            onTabOut((e as any).shiftKey ? 'prev' : 'next');
                        }
                      }}
                      onBlur={() => {
                        if (parId.trim() === '') {
                          setParIdError(true);
                          return;
                        }
                        handleSave();
                      }}
                    />
                    {parIdError && (
                      <span className={styles.errorText} role="alert">
                        PAR ID is required
                      </span>
                    )}
                    {!parIdError && PAR_ID_MAX_LENGTH - parId.length <= 5 && (
                      <span
                        className={
                          parId.length >= PAR_ID_MAX_LENGTH
                            ? styles.charLimitReached
                            : styles.charLimitWarning
                        }
                        aria-live="polite"
                      >
                        {parId.length >= PAR_ID_MAX_LENGTH
                          ? `Max ${PAR_ID_MAX_LENGTH} characters`
                          : `${PAR_ID_MAX_LENGTH - parId.length} remaining`}
                      </span>
                    )}
                  </div>
                ) : content.fieldType === 'name' ? (
                  // Name editing fields
                  <>
                    <div className={styles.fieldGroup}>
                      <div className={styles.fieldLabel}>FEATURE NAME *</div>
                      <TextField
                        value={featureName}
                        onChange={setFeatureName}
                        placeholder={
                          content.placeholderText || 'Enter feature name'
                        }
                        UNSAFE_className={styles.nameInput}
                        aria-label="Feature Name"
                        onKeyDown={e => {
                          if ((e as any).key === 'Enter') {
                            e.preventDefault();
                            handleSave();
                          } else if (
                            (e as any).key === 'Tab' &&
                            (e as any).shiftKey
                          ) {
                            e.preventDefault();
                            handleSave();
                            if (onTabOut) onTabOut('prev');
                          }
                        }}
                      />
                    </div>

                    <div className={styles.fieldGroup}>
                      <div className={styles.fieldLabel}>LINK (Optional)</div>
                      <TextField
                        value={jiraLink}
                        onChange={setJiraLink}
                        placeholder="Enter URL"
                        UNSAFE_className={styles.urlInput}
                        aria-label="JIRA Link"
                        onKeyDown={e => {
                          if ((e as any).key === 'Enter') {
                            e.preventDefault();
                            handleSave();
                          } else if ((e as any).key === 'Tab') {
                            e.preventDefault();
                            const direction = (e as any).shiftKey
                              ? 'prev'
                              : 'next';
                            handleSave();
                            if (onTabOut) onTabOut(direction);
                          }
                        }}
                      />
                      {urlError && (
                        <div className={styles.errorText} role="alert">
                          Please enter a valid URL.
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  // Demo/PRD fields (existing)
                  <>
                    {/* ETA Date Field - using exact RSD DateComponent */}
                    <div className={styles.fieldGroup}>
                      <div className={styles.fieldLabel}>
                        {fieldTypeLabel} ETA
                      </div>
                      <div className={styles.dateFieldWrapper}>
                        <DateComponent
                          field={{
                            id: `${content.fieldType}-eta`,
                            name: `${content.fieldType}Eta`,
                            label: `${fieldTypeLabel} ETA`,
                            type: 'datepicker',
                            required: false
                          }}
                          value={etaDate}
                          onChange={handleDateChange}
                          hideLabel={true}
                          minimal={true}
                          showTBDNA={true}
                          disableTBD={content.disableTBD ?? true}
                          disableNA={content.disableNA ?? true}
                        />
                        {/* Note: Enter handling added at document level below for demo/prd */}
                      </div>
                    </div>

                    {/* URL Field */}
                    <div className={styles.fieldGroup}>
                      <div className={styles.fieldLabel}>
                        {fieldTypeLabel} LINK
                      </div>
                      <TextField
                        value={url}
                        onChange={setUrl}
                        placeholder="https://"
                        UNSAFE_className={styles.urlInput}
                        aria-label={`${fieldTypeLabel} Link`}
                        onKeyDown={e => {
                          if ((e as any).key === 'Enter') {
                            e.preventDefault();
                            handleSaveAndClose();
                          } else if (
                            (e as any).key === 'Tab' &&
                            !(e as any).shiftKey
                          ) {
                            // Tab from URL field (last field) → move to next field
                            e.preventDefault();
                            handleSave();
                            if (onTabOut) onTabOut('next');
                          }
                        }}
                        onBlur={() => handleSave()}
                      />
                      {prdDemoUrlError && (
                        <div className={styles.errorText} role="alert">
                          {demoUrlError
                            ? DEMO_LINK_ERROR_MESSAGE
                            : 'Please enter a valid URL.'}
                        </div>
                      )}
                    </div>
                    <div className={styles.helperNote}>
                      Minimum 1 Field is Required
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Autofocus first focusable field when popup opens
  React.useEffect(() => {
    if (!isVisible) return;
    const root = containerRef.current;
    if (!root) return;
    const focusFirst = () => {
      const selector =
        content.fieldType === 'name' || content.fieldType === 'parId'
          ? 'input, textarea'
          : 'button, [role="button"], input, textarea, [tabindex]:not([tabindex="-1"])';
      const el = root.querySelector(selector) as HTMLElement | null;
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
    };
    // Defer to allow children to render
    setTimeout(focusFirst, 0);
  }, [isVisible, content.fieldType, content.featureId]);

  // Close on outside click (and save before close)
  React.useEffect(() => {
    if (!isVisible) return;

    const handleDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const popupElement = containerRef.current;

      if (!popupElement) return;

      // Check if click is inside the popup
      const isInsidePopup = popupElement.contains(target);

      // Ignore clicks that occur inside Spectrum calendar/popover elements (for demo/prd popups)
      const inCalendarPopover = !!(
        target.closest('.spectrum-Popover') ||
        target.closest('[role="dialog"]') ||
        target.closest('.react-spectrum-Popover') ||
        target.closest('[data-testid*="calendar"]') ||
        target.closest('[data-testid*="date-picker"]')
      );

      // Don't close if clicking inside the popup, calendar/date picker, or the trigger element
      const isTriggerClick = !!(
        triggerRef?.current && triggerRef.current.contains(target)
      );
      if (isInsidePopup || inCalendarPopover || isTriggerClick) {
        return;
      }

      // Click is outside, save and close the popup
      handleSave();
      if (onRequestClose) onRequestClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSave();
        if (onRequestClose) onRequestClose();
      }
      // Allow TAB for field navigation without closing
      if (e.key === 'Tab') {
        return;
      }
      if (
        e.key === 'Enter' &&
        content.fieldType !== 'name' &&
        content.fieldType !== 'parId'
      ) {
        // Global Enter handler for demo/prd to save & close regardless of focus
        e.preventDefault();
        handleSaveAndClose();
      }
    };

    // Use capture phase to ensure we catch the event early
    document.addEventListener('mousedown', handleDocMouseDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocMouseDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isVisible,
    onRequestClose,
    featureName,
    jiraLink,
    etaDate,
    url,
    parId,
    content.fieldType,
    triggerRef
  ]);

  // Render using a portal to avoid stacking context issues with the sliding panel
  const overlayRoot =
    document.querySelector('[data-testid="slideout-panel"]') ||
    document.querySelector('[data-testid="features-table-card"]') ||
    document.body;

  return isVisible ? createPortal(popupContent, overlayRoot) : null;
};

export default FeatureHoverPopup;
